<template>
  <transition name="fade" mode="out-in">
    <div class="col-span-full sm:col-span-full md:col-span-7">
      <Loader v-if="showLoader" :isBody="true" />
      <h1 class="text-t1 mb-4">Styleguide</h1>
      <div class="col-span-full flex flex-col gap-8 md:col-span-7">
        <!-- Typography -->
        <section class="space-y-4">
          <h2 class="text-t4">Typography</h2>
          <div class="space-y-8">
            <div class="space-y-4">
              <h3 class="text-t6">Headings</h3>
              <div class="space-y-4">
                <h1 class="text-t1">Heading 1 (text-t1)</h1>
                <h2 class="text-t2">Heading 2 (text-t2)</h2>
                <h3 class="text-t3">Heading 3 (text-t3)</h3>
                <h4 class="text-t4">Heading 4 (text-t4)</h4>
                <h5 class="text-t5">Heading 5 (text-t5)</h5>
                <h6 class="text-t6">Heading 6 (text-t6)</h6>
              </div>
            </div>

            <div class="space-y-4">
              <h3 class="text-t6">Body Text</h3>
              <div class="space-y-4">
                <p class="text-preamble">
                  Preamble Text (text-preamble) - Used for introductory paragraphs
                </p>
                <p class="text-b1">Body 1 (text-b1) - Main body text style</p>
                <p class="text-b2">Body 2 (text-b2) - Secondary body text style</p>
                <p class="text-b3">Body 3 (text-b3) - Small text style</p>
              </div>
            </div>

            <div class="space-y-4">
              <h3 class="text-t6">Special Text Styles</h3>
              <div class="space-y-4">
                <p class="text-label">Label Text (text-label)</p>
                <p class="text-caption">Caption Text (text-caption)</p>
                <p class="text-date">Date Text (text-date)</p>
                <p class="text-form-label">Form Label (text-form-label)</p>
                <p class="text-form-input">Form Input Text (text-form-input)</p>
                <div class="flex gap-4">
                  <p class="text-button-small">Button Small Text (text-button-small)</p>
                  <p class="text-button-large">Button Large Text (text-button-large)</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Colors -->
        <section class="space-y-4">
          <h2 class="text-t4">Colors</h2>

          <!-- Primary Colors -->
          <div class="space-y-4">
            <h3 class="text-t6">Primary Colors</h3>
            <div class="grid grid-cols-2 gap-4 md:grid-cols-4">
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-primary"></div>
                <p class="text-b2">Primary</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-primary-10"></div>
                <p class="text-b2">Primary 10</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-primary-30"></div>
                <p class="text-b2">Primary 30</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-yellow"></div>
                <p class="text-b2">Yellow</p>
              </div>
            </div>
          </div>

          <!-- Turquoise Colors -->
          <div class="space-y-4">
            <h3 class="text-t6">Turquoise Colors</h3>
            <div class="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-5">
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-turquoise"></div>
                <p class="text-b2">Turquoise</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-turquoise-5"></div>
                <p class="text-b2">Turquoise 5</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-turquoise-10"></div>
                <p class="text-b2">Turquoise 10</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-turquoise-20"></div>
                <p class="text-b2">Turquoise 20</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-turquoise-30"></div>
                <p class="text-b2">Turquoise 30</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-turquoise-40"></div>
                <p class="text-b2">Turquoise 40</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-turquoise-50"></div>
                <p class="text-b2">Turquoise 50</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-turquoise-60"></div>
                <p class="text-b2">Turquoise 60</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-turquoise-70"></div>
                <p class="text-b2">Turquoise 70</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-turquoise-80"></div>
                <p class="text-b2">Turquoise 80</p>
              </div>
            </div>
          </div>

          <!-- Dark Turquoise Colors -->
          <div class="space-y-4">
            <h3 class="text-t6">Dark Turquoise Colors</h3>
            <div class="grid grid-cols-2 gap-4 md:grid-cols-4">
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-dark-turquoise"></div>
                <p class="text-b2">Dark Turquoise</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-dark-turquoise-10"></div>
                <p class="text-b2">Dark Turquoise 10</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-dark-turquoise-40"></div>
                <p class="text-b2">Dark Turquoise 40</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-dark-turquoise-70"></div>
                <p class="text-b2">Dark Turquoise 70</p>
              </div>
            </div>
          </div>

          <!-- Accent Colors -->
          <div class="space-y-4">
            <h3 class="text-t6">Accent Colors</h3>
            <div class="grid grid-cols-2 gap-4 md:grid-cols-4">
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-cerise"></div>
                <p class="text-b2">Cerise</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-wine-red"></div>
                <p class="text-b2">Wine Red</p>
              </div>
            </div>
          </div>

          <!-- Purple Colors -->
          <div class="space-y-4">
            <h3 class="text-t6">Purple Colors</h3>
            <div class="grid grid-cols-2 gap-4 md:grid-cols-4">
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-purple"></div>
                <p class="text-b2">Purple</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-purple-10"></div>
                <p class="text-b2">Purple 10</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-purple-20"></div>
                <p class="text-b2">Purple 20</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-purple-40"></div>
                <p class="text-b2">Purple 40</p>
              </div>
            </div>
          </div>

          <!-- Green Colors -->
          <div class="space-y-4">
            <h3 class="text-t6">Green Colors</h3>
            <div class="grid grid-cols-2 gap-4 md:grid-cols-4">
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-green"></div>
                <p class="text-b2">Green</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-green-light"></div>
                <p class="text-b2">Green Light</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-green-dark"></div>
                <p class="text-b2">Green Dark</p>
              </div>
            </div>
          </div>

          <!-- Status Colors -->
          <div class="space-y-4">
            <h3 class="text-t6">Status Colors</h3>
            <div class="grid grid-cols-2 gap-4 md:grid-cols-4">
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-danger"></div>
                <p class="text-b2">Danger</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-danger-10"></div>
                <p class="text-b2">Danger 10</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-success"></div>
                <p class="text-b2">Success</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-success-10"></div>
                <p class="text-b2">Success 10</p>
              </div>
            </div>
          </div>

          <!-- Gray Colors -->
          <div class="space-y-4">
            <h3 class="text-t6">Gray Colors</h3>
            <div class="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-5">
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-gray"></div>
                <p class="text-b2">Gray</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-gray-5"></div>
                <p class="text-b2">Gray 5</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-gray-10"></div>
                <p class="text-b2">Gray 10</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-gray-20"></div>
                <p class="text-b2">Gray 20</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-gray-30"></div>
                <p class="text-b2">Gray 30</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-gray-40"></div>
                <p class="text-b2">Gray 40</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-gray-50"></div>
                <p class="text-b2">Gray 50</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-gray-60"></div>
                <p class="text-b2">Gray 60</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-gray-70"></div>
                <p class="text-b2">Gray 70</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-gray-80"></div>
                <p class="text-b2">Gray 80</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-gray-90"></div>
                <p class="text-b2">Gray 90</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-gray-light"></div>
                <p class="text-b2">Gray Light</p>
              </div>
              <div class="space-y-2">
                <div class="h-20 rounded-[15px] bg-gray-dark"></div>
                <p class="text-b2">Gray Dark</p>
              </div>
            </div>
          </div>
        </section>

        <!-- Box Component -->
        <section class="space-y-4">
          <h2 class="text-t4">Box Component</h2>
          <div class="space-y-4">
            <Box>
              <h3 class="text-t6 mb-4">Basic Box</h3>
              <p class="text-b2">This is a basic box component with default styling.</p>
            </Box>

            <Box>
              <div class="flex items-center justify-between">
                <div>
                  <h3 class="text-t6 mb-2">Box with Header</h3>
                  <p class="text-b2">Box component with header and action button.</p>
                </div>
                <Button variant="secondary" size="small">Action</Button>
              </div>
            </Box>
          </div>
        </section>

        <!-- Icons -->
        <section class="space-y-4">
          <h2 class="text-t4">Icons</h2>
          <div class="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6">
            <div
              v-for="icon in icons"
              :key="icon.name"
              class="flex flex-col items-center gap-2 rounded-[15px] border border-gray-light p-4"
            >
              <component :is="icon.component" class="h-6 w-6" />
              <p class="text-b3 text-center">{{ icon.name }}</p>
            </div>
          </div>
        </section>

        <!-- Buttons -->
        <section class="space-y-4">
          <h2 class="text-t4">Buttons</h2>

          <!-- Standard Buttons -->
          <div class="space-y-4">
            <h3 class="text-t6">Standard Buttons</h3>
            <div class="flex flex-wrap gap-4">
              <Button variant="primary">Primary Button</Button>
              <Button variant="secondary">Secondary Button</Button>
              <Button variant="tertiary">Tertiary Button</Button>
              <Button variant="danger">Danger Button</Button>
              <Button variant="success">Success Button</Button>
              <Button variant="text">Text Button</Button>
            </div>
          </div>

          <!-- Button Sizes -->
          <div class="space-y-4">
            <h3 class="text-t6">Button Sizes</h3>
            <div class="flex flex-wrap gap-4">
              <Button variant="primary" size="small">Small Button</Button>
              <Button variant="primary" size="large">Large Button</Button>
            </div>
          </div>

          <!-- Button States -->
          <div class="space-y-4">
            <h3 class="text-t6">Button States</h3>
            <div class="flex flex-wrap gap-4">
              <Button variant="primary" disabled>Disabled Button</Button>
              <Button variant="secondary" disabled>Disabled Secondary</Button>
              <Button variant="tertiary" disabled>Disabled Tertiary</Button>
            </div>
          </div>

          <!-- Buttons with Icons -->
          <div class="space-y-4">
            <h3 class="text-t6">Buttons with Icons</h3>
            <div class="flex flex-wrap gap-4">
              <Button variant="primary">
                <template #icon-before>
                  <IconArrowRight />
                </template>
                Primary button Icon Before
              </Button>
              <Button variant="primary">
                Primary buttonIcon After
                <template #icon-after>
                  <IconArrowRight />
                </template>
              </Button>
              <Button variant="secondary">
                <template #icon-before>
                  <IconArrowRight />
                </template>
                Secondary button Icon Before
              </Button>
              <Button variant="secondary">
                Secondary button Icon After
                <template #icon-after>
                  <IconArrowRight />
                </template>
              </Button>
              <Button variant="danger">
                <template #icon-before>
                  <IconArrowRight />
                </template>
                Danger button Icon Before
              </Button>
              <Button variant="danger">
                Danger button Icon After
                <template #icon-after>
                  <IconArrowRight />
                </template>
              </Button>
              <Button variant="tertiary">
                <template #icon-before>
                  <IconArrowRight />
                </template>
                Tertiary button Icon Before
              </Button>
              <Button variant="tertiary">
                Tertiary button Icon After
                <template #icon-after>
                  <IconArrowRight />
                </template>
              </Button>
              <Button variant="text">
                <template #icon-before>
                  <IconArrowRight />
                </template>
                Text button Icon Before
              </Button>
              <Button variant="text">
                Text button Icon After
                <template #icon-after>
                  <IconArrowRight />
                </template>
              </Button>
            </div>
          </div>

          <!-- Social Buttons -->
          <div class="space-y-4">
            <h3 class="text-t6">Social Buttons</h3>
            <div class="flex max-w-md flex-col gap-4">
              <Button variant="social">
                <IconGoogle class="h-6 w-6" />
                Continue with Google
              </Button>
              <Button variant="social">
                <IconMicrosoft class="h-6 w-6" />
                Continue with Microsoft
              </Button>
              <Button variant="social">
                <IconApple class="h-6 w-6" />
                Continue with Apple
              </Button>
              <Button variant="social" state="active">
                <IconBankID class="h-6 w-6" />
                Continue with BankID
              </Button>
              <Button variant="social" state="cannot-remove">
                <IconBankID class="h-6 w-6" />
                Cannot Remove State
              </Button>
            </div>
          </div>
        </section>

        <!-- Forms -->
        <section class="space-y-4">
          <h2 class="text-t4">Form Components</h2>

          <!-- Text Input -->
          <div class="space-y-4">
            <h3 class="text-t6">Text Input</h3>
            <div class="grid max-w-md gap-4">
              <Input v-model="formExample.text" label="Text Input" placeholder="Enter some text" />
              <Input
                v-model="formExample.email"
                type="email"
                label="Email Input"
                placeholder="Enter your email"
              />
              <Input
                v-model="formExample.password"
                type="password"
                label="Password Input"
                placeholder="Enter your password"
              />
              <Input
                v-model="formExample.disabled"
                label="Disabled Input"
                placeholder="This input is disabled"
                disabled
              />
              <Input
                v-model="formExample.error"
                label="Error Input"
                placeholder="Input with error"
                error="This field has an error"
              />
            </div>
          </div>

          <!-- Select -->
          <div class="space-y-4">
            <h3 class="text-t6">Select</h3>
            <div class="grid max-w-md gap-4">
              <Select
                v-model="formExample.select"
                label="Select Input"
                :options="[
                  { value: '1', label: 'Option 1' },
                  { value: '2', label: 'Option 2' },
                  { value: '3', label: 'Option 3' },
                ]"
              />
              <Select
                v-model="formExample.selectDisabled"
                label="Disabled Select"
                :options="[
                  { value: '1', label: 'Option 1' },
                  { value: '2', label: 'Option 2' },
                ]"
                disabled
              />
            </div>
          </div>

          <!-- Checkbox -->
          <div class="space-y-4">
            <h3 class="text-t6">Checkbox</h3>
            <div class="grid max-w-md gap-4">
              <Checkbox v-model="formExample.checkbox1" label="Basic Checkbox" />
              <Checkbox v-model="formExample.checkbox2" label="Disabled Checkbox" disabled />
              <Checkbox
                v-model="formExample.checkbox3"
                label="Checkbox with Error"
                error="This checkbox has an error"
              />
            </div>
          </div>

          <!-- Radio -->
          <div class="space-y-4">
            <h3 class="text-t6">Radio Buttons</h3>
            <div class="grid max-w-md gap-4">
              <RadioGroup
                v-model="formExample.radio"
                label="Radio Group"
                :options="[
                  { value: '1', label: 'Option 1' },
                  { value: '2', label: 'Option 2' },
                  { value: '3', label: 'Option 3' },
                ]"
              />
              <RadioGroup
                v-model="formExample.radioDisabled"
                label="Disabled Radio Group"
                :options="[
                  { value: '1', label: 'Option 1' },
                  { value: '2', label: 'Option 2' },
                ]"
                disabled
              />
            </div>
          </div>
        </section>

        <!-- Tables -->
        <section class="space-y-4">
          <h2 class="text-t4">Tables</h2>

          <!-- Basic Table -->
          <div class="space-y-4">
            <h3 class="text-t6">Basic Table</h3>
            <div class="text-base grid grid-cols-2 gap-x-8 gap-y-4">
              <div class="flex">
                <p class="font-semibold">Column 1:</p>
                <p class="ml-2">Value 1</p>
              </div>
              <div class="flex">
                <p class="font-semibold">Column 2:</p>
                <p class="ml-2">Value 2</p>
              </div>
              <div class="flex">
                <p class="font-semibold">Column 3:</p>
                <p class="ml-2">Value 3</p>
              </div>
              <div class="flex">
                <p class="font-semibold">Column 4:</p>
                <p class="ml-2">Value 4</p>
              </div>
            </div>
          </div>

          <!-- Responsive Grid Table -->
          <div class="space-y-4">
            <h3 class="text-t6">Responsive Grid Table with Border</h3>
            <div class="relative rounded-[15px] border border-solid border-gray-light bg-white">
              <div class="p-6">
                <h4 class="text-t6 mb-4">Table Header</h4>
                <div class="text-base grid grid-cols-2 gap-x-8 gap-y-4">
                  <div class="flex">
                    <p class="font-semibold">Objektnummer:</p>
                    <p class="ml-2">3022-002-02-0021</p>
                  </div>
                  <div class="flex">
                    <p class="font-semibold">Hyra:</p>
                    <p class="ml-2">8068 kr</p>
                  </div>
                  <div class="flex">
                    <p class="font-semibold">Lägenhetsnummer:</p>
                    <p class="ml-2">1504</p>
                  </div>
                  <div class="flex">
                    <p class="font-semibold">Kontraktet gäller:</p>
                    <p class="ml-2">2023-04-01 – Tillsvidare</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Invoice-style Table -->
          <div class="space-y-4">
            <h3 class="text-t6">Invoice-style Table</h3>
            <div class="relative rounded-[15px] bg-gray-10 ring-1 ring-gray-light">
              <div class="flex items-center justify-between px-6 py-2">
                <div class="flex items-center gap-8">
                  <div class="border-r border-gray-light pr-8">
                    <p class="text-b1">2023-09-31</p>
                  </div>
                  <div class="flex gap-8">
                    <p class="text-b1">4500 kr</p>
                  </div>
                  <div class="flex gap-8">
                    <p class="text-b1">Autogiro</p>
                  </div>
                </div>
                <div class="text-label absolute -top-100 right-6 rounded-sm">
                  <span class="bg-primary-30 px-2 py-1">Status</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Traditional Table -->
          <div class="space-y-4">
            <h3 class="text-t6">Traditional Table</h3>
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead>
                  <tr class="border-b border-gray-light">
                    <th class="font-semibold p-4 text-left">Date</th>
                    <th class="font-semibold p-4 text-left">Amount</th>
                    <th class="font-semibold p-4 text-left">Payment Method</th>
                    <th class="font-semibold p-4 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="border-b border-gray-light">
                    <td class="p-4">2023-09-31</td>
                    <td class="p-4">4500 kr</td>
                    <td class="p-4">Autogiro</td>
                    <td class="p-4">
                      <span class="text-label rounded-sm bg-primary-30 px-2 py-1">Pending</span>
                    </td>
                  </tr>
                  <tr class="border-b border-gray-light">
                    <td class="p-4">2023-08-31</td>
                    <td class="p-4">4500 kr</td>
                    <td class="p-4">Autogiro</td>
                    <td class="p-4">
                      <span class="text-label rounded-sm bg-green-light px-2 py-1">Paid</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <!-- Expandable -->
        <section class="space-y-4">
          <h2 class="text-t4">Expandable</h2>
          <div class="space-y-4">
            <Expandable v-model:expanded="expandableExample1">
              <template #default="{ toggle, expanded }">
                <div class="flex cursor-pointer items-center justify-between" @click="toggle">
                  <h3 class="text-t6">Click to Expand</h3>
                  <IconChevronDown :class="{ 'rotate-180': expanded }" />
                </div>
              </template>
              <template #body>
                <div class="pt-4">
                  <p class="text-b2">This is the expanded content.</p>
                </div>
              </template>
            </Expandable>

            <Expandable v-model:expanded="expandableExample2">
              <template #default="{ toggle, expanded }">
                <div class="flex cursor-pointer items-center justify-between" @click="toggle">
                  <h3 class="text-t6">Another Expandable</h3>
                  <IconChevronDown :class="{ 'rotate-180': expanded }" />
                </div>
              </template>
              <template #body>
                <div class="pt-4">
                  <p class="text-b2">More expanded content here.</p>
                </div>
              </template>
            </Expandable>
          </div>
        </section>

        <!-- Modal -->
        <section class="space-y-4">
          <h2 class="text-t4">Modal</h2>
          <div class="flex flex-wrap gap-4">
            <Button variant="primary" @click="showBasicModal = true">Show Basic Modal</Button>
            <Button variant="primary" @click="showStepperModal = true">Show Stepper Modal</Button>
            <Button variant="primary" @click="showOnboardingModal = true">Visa onboarding</Button>
          </div>
        </section>

        <!-- Basic Modal Example -->
        <Modal
          v-if="showBasicModal"
          header="Basic Modal"
          confirmText="Confirm"
          cancelText="Cancel"
          v-on:confirm="showBasicModal = false"
          v-on:cancel="showBasicModal = false"
          hasClose
        >
          <p>This is a basic modal example.</p>
        </Modal>

        <!-- Stepper Modal Example -->
        <Modal
          v-if="showStepperModal"
          :is-stepper="true"
          :steps="exampleSteps"
          v-on:confirm="showStepperModal = false"
          v-on:cancel="showStepperModal = false"
          hasClose
        >
          <template #step-0="{ step }">
            <p>This is step 1 content</p>
          </template>
          <template #step-1="{ step }">
            <p>This is step 2 content</p>
          </template>
          <template #step-2="{ step }">
            <p>This is the final step</p>
          </template>
        </Modal>

        <!-- Onboarding Modal -->
        <OnboardingModal
          v-if="showOnboardingModal"
          @close="showOnboardingModal = false"
        />
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters } from 'vuex';
import { Form, Field, ErrorMessage } from 'vee-validate';
import Modal from '@/components/ui/modal';
import Loader from '@/components/ui/loader';
import { markRaw } from 'vue';

import Button from '@/components/ui/button';
import Box from '@/components/ui/box';

// Import all icons
import IconPlus from '@/components/icons/IconPlus.vue';
import IconArrowRight from '@/components/icons/IconArrowRight.vue';
import IconClose from '@/components/icons/IconClose.vue';
import IconEdit from '@/components/icons/IconEdit.vue';
import IconFloorPlan from '@/components/icons/IconFloorPlan.vue';
import IconBankID from '@/components/icons/IconBankID.vue';
import IconCheck from '@/components/icons/IconCheck.vue';
import IconEmail from '@/components/icons/IconEmail.vue';
import IconPhone from '@/components/icons/IconPhone.vue';
import IconHouse from '@/components/icons/IconHouse.vue';
import IconTrashcan from '@/components/icons/IconTrashCan.vue';
import IconExternal from '@/components/icons/IconExternal.vue';
import IconChevronDown from '@/components/icons/IconChevronDown.vue';
import IconChevronLeft from '@/components/icons/IconChevronLeft.vue';
import IconChevronRight from '@/components/icons/IconChevronRight.vue';
import IconBullet from '@/components/icons/IconBullet.vue';
import IconGavleGardarna from '@/components/icons/IconGavleGardarna.vue';
import IconGoogle from '@/components/icons/IconGoogle.vue';
import IconMicrosoft from '@/components/icons/IconMicrosoft.vue';
import IconApple from '@/components/icons/IconApple.vue';
import IconReload from '@/components/icons/IconReload.vue';
import IconQuePoints from '@/components/icons/IconQuePoints.vue';
import IconStorage from '@/components/icons/IconStorage.vue';
import IconHouseBlank from '@/components/icons/IconHouseBlank.vue';
import IconInvoice from '@/components/icons/IconInvoice.vue';
import IconEmailBlank from '@/components/icons/IconEmailBlank.vue';
import IconGreenCheckmark from '@/components/icons/IconGreenCheckmark.vue';
import IconCalendarBlank from '@/components/icons/IconCalendarBlank.vue';
import IconCar from '@/components/icons/IconCar.vue';
import IconBoxEmptyBlank from '@/components/icons/IconBoxEmptyBlank.vue';
import IconLaundry from '@/components/icons/IconLaundry.vue';
import IconTools from '@/components/icons/IconTools.vue';
import Input from '@/components/ui/form/input';
import Select from '@/components/ui/form/select';
import Checkbox from '@/components/ui/form/checkbox';
import Radio from '@/components/ui/form/radio';
import RadioGroup from '@/components/ui/form/radio-group';
import Expandable from '@/components/ui/expandable';
import OnboardingModal from '@/pages/dashboard/onboarding-modal';

export default {
  name: 'Styleguide',
  components: {
    Modal,
    Loader,
    Button,
    Box,
    IconPlus,
    IconArrowRight,
    IconClose,
    IconEdit,
    IconFloorPlan,
    IconBankID,
    IconCheck,
    IconEmail,
    IconPhone,
    IconHouse,
    IconTrashcan,
    IconExternal,
    IconChevronDown,
    IconChevronLeft,
    IconChevronRight,
    IconBullet,
    IconGavleGardarna,
    IconGoogle,
    IconMicrosoft,
    IconApple,
    IconReload,
    IconQuePoints,
    IconStorage,
    IconHouseBlank,
    IconInvoice,
    IconEmailBlank,
    IconGreenCheckmark,
    IconCalendarBlank,
    IconCar,
    IconBoxEmptyBlank,
    IconLaundry,
    IconTools,
    Input,
    Select,
    Checkbox,
    Radio,
    RadioGroup,
    Expandable,
    OnboardingModal,
  },
  data() {
    return {
      showLoader: false,
      showBasicModal: false,
      showStepperModal: false,
      showOnboardingModal: false,
      exampleSteps: [
        {
          title: 'Step 1',
          nextButtonText: 'Continue to Step 2',
          backButtonText: 'Cancel',
        },
        {
          title: 'Step 2',
          nextButtonText: 'Continue to Final Step',
          backButtonText: 'Back to Step 1',
        },
        {
          title: 'Final Step',
          confirmButtonText: 'Complete',
          backButtonText: 'Back to Step 2',
        },
      ],
      icons: [
        { name: 'Plus', component: markRaw(IconPlus) },
        { name: 'Arrow Right', component: markRaw(IconArrowRight) },
        { name: 'Close', component: markRaw(IconClose) },
        { name: 'Edit', component: markRaw(IconEdit) },
        { name: 'Floor Plan', component: markRaw(IconFloorPlan) },
        { name: 'BankID', component: markRaw(IconBankID) },
        { name: 'Check', component: markRaw(IconCheck) },
        { name: 'Email', component: markRaw(IconEmail) },
        { name: 'Phone', component: markRaw(IconPhone) },
        { name: 'House', component: markRaw(IconHouse) },
        { name: 'Trash', component: markRaw(IconTrashcan) },
        { name: 'External', component: markRaw(IconExternal) },
        { name: 'Chevron Down', component: markRaw(IconChevronDown) },
        { name: 'Chevron Left', component: markRaw(IconChevronLeft) },
        { name: 'Chevron Right', component: markRaw(IconChevronRight) },
        { name: 'Bullet', component: markRaw(IconBullet) },
        { name: 'Gävle Gårdarna', component: markRaw(IconGavleGardarna) },
        { name: 'Google', component: markRaw(IconGoogle) },
        { name: 'Microsoft', component: markRaw(IconMicrosoft) },
        { name: 'Apple', component: markRaw(IconApple) },
        { name: 'Reload', component: markRaw(IconReload) },
        { name: 'Que Points', component: markRaw(IconQuePoints) },
        { name: 'Storage', component: markRaw(IconStorage) },
        { name: 'House Blank', component: markRaw(IconHouseBlank) },
        { name: 'Invoice', component: markRaw(IconInvoice) },
        { name: 'Email Blank', component: markRaw(IconEmailBlank) },
        { name: 'Green Checkmark', component: markRaw(IconGreenCheckmark) },
        { name: 'Calendar Blank', component: markRaw(IconCalendarBlank) },
        { name: 'Car', component: markRaw(IconCar) },
        { name: 'Box Empty Blank', component: markRaw(IconBoxEmptyBlank) },
        { name: 'Laundry', component: markRaw(IconLaundry) },
        { name: 'Tools', component: markRaw(IconTools) },
      ],
      formExample: {
        text: '',
        email: '',
        password: '',
        disabled: '',
        error: '',
        select: '',
        selectDisabled: '',
        checkbox1: false,
        checkbox2: false,
        checkbox3: false,
        radio: '',
        radioDisabled: '',
      },
      expandableExample1: false,
      expandableExample2: false,
    };
  },
  computed: {
    ...mapGetters({}),
  },

  mounted() {},

  methods: {},
};
</script>
